import React, { useEffect, useMemo, useState } from 'react';
import { getTradingPlan } from '../services/api';
import './Plan.css';

const parsePlanContent = (content) => {
  if (!content) {
    return [];
  }

  const lines = content.split(/\r?\n/);
  const blocks = [];
  let currentList = null;
  let currentParagraph = [];
  let currentTable = null;

  const flushParagraph = () => {
    if (currentParagraph.length === 0) {
      return;
    }
    blocks.push({ type: 'paragraph', text: currentParagraph.join(' ') });
    currentParagraph = [];
  };

  const flushList = () => {
    if (!currentList || currentList.length === 0) {
      currentList = null;
      return;
    }
    blocks.push({ type: 'list', items: currentList });
    currentList = null;
  };

  const flushTable = () => {
    if (!currentTable || currentTable.headers.length === 0) {
      currentTable = null;
      return;
    }
    blocks.push({ type: 'table', ...currentTable });
    currentTable = null;
  };

  const parseTableRow = (line) => {
    let trimmed = line.trim();
    if (trimmed.startsWith('|')) {
      trimmed = trimmed.slice(1);
    }
    if (trimmed.endsWith('|')) {
      trimmed = trimmed.slice(0, -1);
    }
    return trimmed.split('|').map(cell => cell.trim());
  };

  const isTableSeparator = (line) => {
    const trimmed = line.trim();
    if (!trimmed) return false;
    return trimmed
      .replace(/^\|/, '')
      .replace(/\|$/, '')
      .split('|')
      .every(cell => /^:?-{3,}:?$/.test(cell.trim()));
  };

  lines.forEach((rawLine) => {
    const line = rawLine.replace(/\s+$/, '');

    if (!line.trim()) {
      flushParagraph();
      flushList();
      flushTable();
      return;
    }

    const headingMatch = line.match(/^#\s+(.*)$/);
    if (headingMatch) {
      flushParagraph();
      flushList();
      flushTable();
      blocks.push({ type: 'heading', text: headingMatch[1].trim() });
      return;
    }

    if (line.includes('|') && isTableSeparator(line)) {
      return;
    }

    if (line.includes('|')) {
      const row = parseTableRow(line);
      if (!currentTable) {
        flushParagraph();
        flushList();
        currentTable = { headers: row, rows: [] };
      } else {
        currentTable.rows.push(row);
      }
      return;
    }

    const listMatch = line.match(/^(\s*)(\d+(?:\.\d+)?)\.\s+(.*)$/);
    if (listMatch) {
      flushTable();
      flushParagraph();
      const indent = listMatch[1].length;
      const number = listMatch[2];
      const text = listMatch[3].trim();

      if (!currentList) {
        currentList = [];
      }

      if (indent > 0 && currentList.length > 0) {
        currentList[currentList.length - 1].subitems.push({ number, text });
      } else {
        currentList.push({ text, subitems: [] });
      }
      return;
    }

    flushTable();
    currentParagraph.push(line.trim());
  });

  flushParagraph();
  flushList();
  flushTable();

  return blocks;
};

const Plan = () => {
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let isMounted = true;

    const loadPlan = async () => {
      try {
        setLoading(true);
        const response = await getTradingPlan();
        if (isMounted) {
          setContent(response.data || '');
          setError('');
        }
      } catch (err) {
        if (isMounted) {
          setError(err?.response?.data?.error || 'Failed to load trading plan.');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadPlan();

    return () => {
      isMounted = false;
    };
  }, []);

  const blocks = useMemo(() => parsePlanContent(content), [content]);

  const renderCellContent = (cell) => {
    if (cell === null || cell === undefined) {
      return null;
    }
    const parts = String(cell).split(/<br\s*\/?>|\n/i).map(part => part.trim()).filter(Boolean);
    if (parts.length <= 1) {
      return cell;
    }
    return parts.map((part, index) => {
      const isSubpoint = /^\d+\.\d+\./.test(part);
      const className = isSubpoint ? 'plan-cell-line plan-cell-indent' : 'plan-cell-line';
      return (
        <div key={`cell-line-${index}`} className={className}>
          {part}
        </div>
      );
    });
  };

  return (
    <div className="plan-page container-xl">
      <div className="plan-card">
        <div className="plan-header">
        </div>
        {loading && <div className="plan-status">Loading...</div>}
        {!loading && error && <div className="plan-status plan-error">{error}</div>}
        {!loading && !error && blocks.length === 0 && (
          <div className="plan-status">No plan content found.</div>
        )}
        {!loading && !error && blocks.length > 0 && (
          <div className="plan-content">
            {blocks.map((block, index) => {
              if (block.type === 'heading') {
                return (
                  <h2 key={`heading-${index}`} className="plan-section-title">
                    {block.text}
                  </h2>
                );
              }

              if (block.type === 'list') {
                return (
                  <ol key={`list-${index}`} className="plan-list">
                    {block.items.map((item, itemIndex) => (
                      <li key={`list-item-${index}-${itemIndex}`}>
                        <div className="plan-list-item">{item.text}</div>
                        {item.subitems.length > 0 && (
                          <ul className="plan-sublist">
                            {item.subitems.map((subitem, subIndex) => (
                              <li key={`subitem-${index}-${itemIndex}-${subIndex}`}>
                                <span className="plan-sub-number">{subitem.number}.</span>
                                {subitem.text}
                              </li>
                            ))}
                          </ul>
                        )}
                      </li>
                    ))}
                  </ol>
                );
              }

              if (block.type === 'table') {
                return (
                  <div key={`table-${index}`} className="plan-table-wrapper">
                    <table className="plan-table">
                      <thead>
                        <tr>
                          {block.headers.map((header, headerIndex) => (
                            <th key={`table-${index}-header-${headerIndex}`}>{header}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {block.rows.map((row, rowIndex) => (
                          <tr key={`table-${index}-row-${rowIndex}`}>
                            {row.map((cell, cellIndex) => (
                              <td key={`table-${index}-row-${rowIndex}-cell-${cellIndex}`}>
                                {renderCellContent(cell)}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              }

              if (block.type === 'paragraph') {
                return (
                  <p key={`paragraph-${index}`} className="plan-paragraph">
                    {block.text}
                  </p>
                );
              }

              return null;
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Plan;
